import { currencyCodeSuffix } from '../../../utils/money';
import { useBrandLocale } from '../../../hooks/useBrandLocale';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useEffect, useMemo, useState } from 'react';
import { ScaleLoader } from 'react-spinners';
import { useSnapshot } from 'valtio';
import { authStore } from '../../../../store/authStore';
import { useGetHostEarningsQuery } from '../../../services/hostEarningsService/__generated__/queries.generated';
import { useGetMyCarsV2LeanQuery, GetMyCarsV2Item } from '../../../services/carService/getMyCarsV2';
import { VehicleEarningsStatus } from '../../../../__generated__/graphql';
import { URLS } from '../../../../constants/urls';
import { mergePagedItems } from '../../../utils/hostListingsPagination';

const PageContainer = styled.div`
  ${tw`flex flex-col w-full max-w-full min-w-0 max-w-screen-xl mx-auto pb-10 md:pb-16 px-4 lg:px-12 box-border`}
`;

const Header = styled.div`
  ${tw`flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-6 md:mt-8 mb-8`}
`;

const Title = styled.h1`
  ${tw`text-3xl font-bold text-text-primary`}
`;

const ControlsRow = styled.div`
  ${tw`flex flex-wrap items-center gap-3`}
`;

const Select = styled.select`
  ${tw`border border-border-default rounded-lg px-3 py-2 text-sm bg-white`}
`;

const HeroCard = styled.div`
  ${tw`bg-white rounded-modal border border-border-default shadow-card p-6 md:p-8 mb-6`}
`;

const HeroLabel = styled.p`
  ${tw`text-sm font-medium text-text-secondary uppercase tracking-wide`}
`;

const HeroAmount = styled.p`
  ${tw`text-[2rem] font-bold text-text-primary mt-2`}
`;

const HeroSubtext = styled.p`
  ${tw`text-sm text-text-secondary mt-2`}
`;

const ChartCard = styled.div`
  ${tw`bg-white rounded-modal border border-border-default shadow-card p-6 mb-8 overflow-visible`}
`;

const ChartTitle = styled.h2`
  ${tw`text-lg font-semibold text-text-primary mb-6`}
`;

const ChartWrapper = styled.div`
  ${tw`relative overflow-visible pt-24 -mt-16`}
`;

const ChartInner = styled.div`
  ${tw`flex items-start gap-2 md:gap-3`}
`;

const ChartPlot = styled.div`
  ${tw`relative flex-1 min-w-0`}
`;

const CHART_BAR_HEIGHT_PX = 224;

const ChartBarsFrame = styled.div`
  position: relative;
  height: ${CHART_BAR_HEIGHT_PX}px;
`;

const ChartGrid = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
`;

const GridLine = styled.div<{ $bottomPct: number }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: ${props => props.$bottomPct}%;
  border-top: 1px solid #e5e7eb;
`;

const ChartArea = styled.div`
  ${tw`relative flex items-end justify-between gap-1 md:gap-2 overflow-visible z-[1]`}
  height: ${CHART_BAR_HEIGHT_PX}px;
`;

const ChartMonthLabelsRow = styled.div`
  ${tw`flex justify-between gap-1 md:gap-2 mt-2 border-t border-border-default pt-2`}
`;

const MonthLabelCell = styled.span`
  ${tw`flex-1 text-center text-[10px] md:text-xs text-text-secondary uppercase min-w-0`}
`;

const YAxisColumn = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 2.75rem;
  height: ${CHART_BAR_HEIGHT_PX}px;
  margin-top: 0;
`;

const YAxisLabel = styled.span<{ $bottomPct: number }>`
  position: absolute;
  right: 0;
  bottom: ${props => props.$bottomPct}%;
  transform: translateY(50%);
  ${tw`text-[10px] md:text-xs text-text-secondary tabular-nums leading-none whitespace-nowrap`}
`;

const BarColumn = styled.div`
  ${tw`flex-1 flex flex-col items-center justify-end h-full min-w-0 overflow-visible`}
`;

const BarStack = styled.div<{ $heightPct: number }>`
  /* flex-col-reverse: first DOM segment sits on the x-axis (completed), last segment is on top (reimbursements) */
  ${tw`w-full max-w-[2.5rem] flex flex-col-reverse overflow-visible`}
  height: ${props => `${props.$heightPct}%`};
  min-height: ${props => (props.$heightPct > 0 ? '4px' : '0')};
`;

type ChartSegmentKey = 'completed' | 'upcoming' | 'reimbursements' | 'hostIncentives';

const BarSegment = styled.div<{
  $pct: number;
  $color: string;
  $activeColor: string;
  $isActive: boolean;
  $isTop?: boolean;
}>`
  ${tw`w-full cursor-pointer relative`}
  height: ${props => `${props.$pct}%`};
  min-height: ${props => (props.$pct > 0 ? '3px' : '0')};
  background-color: ${props => (props.$isActive ? props.$activeColor : props.$color)};
  transition: background-color 0.12s ease;
  ${props => props.$isTop && tw`rounded-t-sm`}
`;

const SegmentTooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 30;
  pointer-events: none;
  min-width: 7.5rem;
  background: #fff;
  border: 1px solid #111;
  border-radius: 2px;
  padding: 10px 14px 12px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-top: 7px solid #111;
  }

  &::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-top: -1px;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid #fff;
    z-index: 1;
  }
`;

const TooltipMonth = styled.p`
  ${tw`text-[11px] font-bold text-text-primary uppercase tracking-wide leading-tight`}
`;

const TooltipAmount = styled.p`
  ${tw`text-2xl font-bold text-text-primary leading-tight mt-1`}
`;

const TooltipCategory = styled.p`
  ${tw`text-sm text-text-primary mt-1`}
`;

const BarLabel = styled.span`
  ${tw`sr-only`}
`;

const formatAxisMoney = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value ?? 0);

/** Pick a round step (100, 1k, 10k, …) so the axis ceiling fits the tallest bar with ~4–6 ticks. */
const computeChartYAxis = (maxBarTotal: number) => {
  const safeMax = Math.max(maxBarTotal, 0);

  if (safeMax === 0) {
    return {
      axisMax: 4000,
      step: 1000,
      ticks: [0, 1000, 2000, 3000, 4000],
    };
  }

  const niceMultipliers = [1, 2, 5, 10];
  const minExp = Math.floor(Math.log10(safeMax)) - 1;
  const maxExp = Math.ceil(Math.log10(safeMax)) + 1;

  let best: { axisMax: number; step: number; ticks: number[] } | null = null;

  for (let exp = minExp; exp <= maxExp; exp += 1) {
    const magnitude = 10 ** exp;
    for (const multiplier of niceMultipliers) {
      const step = multiplier * magnitude;
      if (step <= 0) {
        continue;
      }

      for (let tickCount = 4; tickCount <= 6; tickCount += 1) {
        const axisMax = step * tickCount;
        if (axisMax < safeMax) {
          continue;
        }

        const ticks = Array.from({ length: tickCount + 1 }, (_, index) => index * step);
        if (!best || axisMax < best.axisMax || (axisMax === best.axisMax && step < best.step)) {
          best = { axisMax, step, ticks };
        }
      }
    }
  }

  if (best) {
    return best;
  }

  const fallbackStep = 10 ** Math.ceil(Math.log10(safeMax / 4));
  const axisMax = fallbackStep * Math.ceil(safeMax / fallbackStep);
  const tickCount = Math.round(axisMax / fallbackStep);
  return {
    axisMax,
    step: fallbackStep,
    ticks: Array.from({ length: tickCount + 1 }, (_, index) => index * fallbackStep),
  };
};

const ChartLegend = styled.div`
  ${tw`flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-6`}
`;

const LegendItem = styled.span`
  ${tw`flex items-center gap-2 text-xs text-text-secondary`}
`;

const LegendSwatch = styled.span<{ $color: string }>`
  ${tw`inline-block w-3 h-3 rounded-sm flex-shrink-0`}
  background-color: ${props => props.$color};
`;

const LegendAmount = styled.span`
  ${tw`font-semibold text-text-primary`}
`;

const VehicleSection = styled.div`
  ${tw`bg-white rounded-modal border border-border-default shadow-card p-6`}
`;

const VehicleSectionTitle = styled.h2`
  ${tw`text-lg font-semibold text-text-primary mb-4`}
`;

const VehicleList = styled.div`
  ${tw`flex flex-col divide-y divide-border-default`}
`;

const VehicleRow = styled.div`
  ${tw`flex items-center gap-4 py-4`}
`;

const VehicleImage = styled.img`
  ${tw`w-16 h-12 object-cover rounded-lg bg-bg-secondary`}
`;

const VehicleImagePlaceholder = styled.div`
  ${tw`w-16 h-12 rounded-lg bg-bg-secondary flex items-center justify-center text-xs text-text-tertiary`}
`;

const VehicleInfo = styled.div`
  ${tw`flex-1 min-w-0`}
`;

const VehicleName = styled.p`
  ${tw`font-medium text-text-primary truncate`}
`;

const VehicleMeta = styled.p`
  ${tw`text-sm text-text-secondary truncate`}
`;

const VehicleStatus = styled.span<{ $status: VehicleEarningsStatus }>`
  ${tw`text-xs px-2 py-0.5 rounded-full mr-2`}
  ${props => {
    switch (props.$status) {
      case VehicleEarningsStatus.Listed:
        return tw`bg-green-100 text-green-800`;
      case VehicleEarningsStatus.Unlisted:
        return tw`bg-yellow-100 text-yellow-800`;
      case VehicleEarningsStatus.Deleted:
        return tw`bg-bg-secondary text-text-secondary`;
      default:
        return tw`bg-bg-secondary text-text-secondary`;
    }
  }}
`;

const VehicleAmount = styled.p`
  ${tw`text-lg font-semibold text-text-primary whitespace-nowrap`}
`;

const LoadingContainer = styled.div`
  ${tw`w-full flex justify-center items-center mt-20`}
`;

const ErrorText = styled.p`
  ${tw`text-red-500 text-center mt-20`}
`;

const EmptyText = styled.p`
  ${tw`text-text-secondary text-center py-8`}
`;

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const CHART_SEGMENT_CONFIG: Array<{
  key: ChartSegmentKey;
  label: string;
  color: string;
  activeColor: string;
  getValue: (month: {
    completedEarnings?: number | null;
    upcomingEarnings?: number | null;
    reimbursements?: number | null;
    hostIncentives?: number | null;
  }) => number;
}> = [
  {
    key: 'completed',
    label: 'Trip earnings',
    color: '#14b8a6',
    activeColor: '#0d9488',
    getValue: month => month.completedEarnings ?? 0,
  },
  {
    key: 'upcoming',
    label: 'Upcoming earnings',
    color: '#99f6e4',
    activeColor: '#5eead4',
    getValue: month => month.upcomingEarnings ?? 0,
  },
  {
    key: 'reimbursements',
    label: 'Reimbursements',
    color: '#22c55e',
    activeColor: '#16a34a',
    getValue: month => month.reimbursements ?? 0,
  },
  {
    key: 'hostIncentives',
    label: 'Incentives',
    color: '#fb923c',
    activeColor: '#f97316',
    getValue: month => month.hostIncentives ?? 0,
  },
];

const formatMoney = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value ?? 0);

const formatTooltipMoney = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value ?? 0);

const buildYearOptions = () => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 6 }, (_, index) => currentYear - index);
};

const getVehicleImageUrl = (car: {
  thumbnailUrl?: string | null;
  images?: Array<{ imageUrl?: string | null } | null> | null;
}) => {
  const imageKeyOrUrl = car.thumbnailUrl || car.images?.[0]?.imageUrl || null;
  if (!imageKeyOrUrl) {
    return null;
  }

  if (/^https?:\/\//i.test(imageKeyOrUrl)) {
    return imageKeyOrUrl;
  }

  return `${URLS.S3_BUCKET.CAR_THUMBNAILS}/${imageKeyOrUrl.replace(/^\/+/, '')}`;
};

export default function EarningsPage() {
  // Spec 028 D14: earnings are in the brand's currency; CAD totals carry the code.
  const earningsCode = currencyCodeSuffix(useBrandLocale().currency);
  const { isAuthenticated } = useSnapshot(authStore);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>('');
  const [hoveredSegment, setHoveredSegment] = useState<{
    month: number;
    segment: ChartSegmentKey;
  } | null>(null);

  const yearOptions = useMemo(() => buildYearOptions(), []);

  const { data, loading, error } = useGetHostEarningsQuery({
    skip: !isAuthenticated,
    variables: {
      input: {
        year: selectedYear,
        vehicleIds: selectedVehicleId ? [selectedVehicleId] : undefined,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const [vehiclePage, setVehiclePage] = useState(1);
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [vehicleQ, setVehicleQ] = useState('');
  const [filterCars, setFilterCars] = useState<GetMyCarsV2Item[]>([]);
  const [selectedVehicleOption, setSelectedVehicleOption] = useState<GetMyCarsV2Item | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setVehicleQ(vehicleSearch.trim());
      setVehiclePage(1);
      setFilterCars([]);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [vehicleSearch]);

  const {
    data: myCarsData,
    loading: myCarsLoading,
    error: myCarsError,
    refetch: refetchMyCars,
  } = useGetMyCarsV2LeanQuery({
    skip: !isAuthenticated,
    variables: {
      input: {
        page: vehiclePage,
        limit: 20,
        q: vehicleQ.trim() || undefined,
      },
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    const paged = myCarsData?.getMyCarsV2;
    if (!paged || paged.page !== vehiclePage) return;
    setFilterCars(prev => mergePagedItems(prev, paged.page, paged.items));
  }, [myCarsData, vehiclePage]);

  const vehicleHasMore =
    myCarsData?.getMyCarsV2?.page === vehiclePage && myCarsData.getMyCarsV2.hasMore === true;

  const earnings = data?.hostEarnings;
  const monthlyEarnings = earnings?.monthlyEarnings ?? [];
  const vehicleEarnings = earnings?.vehicleEarnings ?? [];

  const maxMonthlyTotal = useMemo(() => {
    const totals = monthlyEarnings.map(month =>
      CHART_SEGMENT_CONFIG.reduce((sum, segment) => sum + segment.getValue(month), 0),
    );
    return Math.max(...totals, 0);
  }, [monthlyEarnings]);

  const chartYAxis = useMemo(() => computeChartYAxis(maxMonthlyTotal), [maxMonthlyTotal]);

  if (!isAuthenticated) {
    return null;
  }

  if (loading && !earnings) {
    return (
      <LoadingContainer>
        <ScaleLoader color="#36d7b7" />
      </LoadingContainer>
    );
  }

  if (error) {
    return <ErrorText>Unable to load earnings: {error.message}</ErrorText>;
  }

  const yearSummary = earnings?.earnings;

  const renderSegmentTooltip = (month: number, segmentKey: ChartSegmentKey, value: number) => {
    const segmentConfig = CHART_SEGMENT_CONFIG.find(item => item.key === segmentKey);
    if (!segmentConfig) {
      return null;
    }

    const monthLabel = MONTH_LABELS[month - 1].toUpperCase();

    return (
      <SegmentTooltip>
        <TooltipMonth>
          {monthLabel} {selectedYear}
        </TooltipMonth>
        <TooltipAmount>{formatTooltipMoney(value)}</TooltipAmount>
        <TooltipCategory>{segmentConfig.label}</TooltipCategory>
      </SegmentTooltip>
    );
  };

  return (
    <PageContainer>
      <Header>
        <Title>Business · Earnings</Title>
        <ControlsRow>
          <Select
            value={selectedYear}
            onChange={event => setSelectedYear(Number(event.target.value))}
            aria-label="Select year"
          >
            {yearOptions.map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Select>

          <input
            type="search"
            value={vehicleSearch}
            placeholder="Search vehicles"
            aria-label="Search vehicles"
            data-testid="earnings-vehicle-search"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            onChange={event => setVehicleSearch(event.target.value)}
          />
          <Select
            value={selectedVehicleId}
            onChange={event => {
              const nextId = event.target.value;
              setSelectedVehicleId(nextId);
              setSelectedVehicleOption(
                nextId
                  ? (filterCars.find(car => car.id === nextId) ?? selectedVehicleOption)
                  : null,
              );
            }}
            aria-label="Filter by vehicle"
            data-testid="earnings-vehicle-filter"
          >
            <option value="">All vehicles</option>
            {selectedVehicleOption &&
              !filterCars.some(car => car.id === selectedVehicleOption.id) && (
                <option value={selectedVehicleOption.id}>
                  {[
                    selectedVehicleOption.year,
                    selectedVehicleOption.make,
                    selectedVehicleOption.model,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                </option>
              )}
            {filterCars.map(car => (
              <option key={car.id} value={car.id}>
                {[car.year, car.make, car.model].filter(Boolean).join(' ')}
              </option>
            ))}
          </Select>
          {vehicleHasMore && (
            <button
              type="button"
              className="text-sm underline text-gray-700"
              disabled={myCarsLoading}
              data-testid="earnings-vehicle-load-more"
              onClick={() => setVehiclePage(p => p + 1)}
            >
              {myCarsLoading ? 'Loading…' : 'Load more vehicles'}
            </button>
          )}
          {myCarsError && (
            <span role="alert" className="text-sm text-red-700">
              Unable to load vehicles.{' '}
              <button type="button" className="underline" onClick={() => void refetchMyCars()}>
                Retry
              </button>
            </span>
          )}
        </ControlsRow>
      </Header>

      <HeroCard>
        <HeroLabel>{selectedYear} trip earnings</HeroLabel>
        <HeroAmount>
          {formatMoney(yearSummary?.completedEarnings ?? 0)}
          {earningsCode}
        </HeroAmount>
        <HeroSubtext>
          Potential earnings {formatMoney(yearSummary?.potentialEarnings ?? 0)}
          {earningsCode} including upcoming trips
        </HeroSubtext>
      </HeroCard>

      <ChartCard>
        <ChartTitle>Monthly earnings</ChartTitle>
        <ChartWrapper>
          <ChartInner>
            <ChartPlot>
              <ChartBarsFrame>
                <ChartGrid>
                  {chartYAxis.ticks.map(tick => (
                    <GridLine
                      key={tick}
                      $bottomPct={(tick / chartYAxis.axisMax) * 100}
                      aria-hidden
                    />
                  ))}
                </ChartGrid>
                <ChartArea>
                  {monthlyEarnings.map(monthData => {
                    const month = monthData.month ?? 1;
                    const segmentValues = CHART_SEGMENT_CONFIG.map(segment => ({
                      ...segment,
                      value: segment.getValue(monthData),
                    }));
                    const total = segmentValues.reduce((sum, segment) => sum + segment.value, 0);
                    const heightPct = (total / chartYAxis.axisMax) * 100;
                    const visibleSegments = segmentValues.filter(segment => segment.value > 0);

                    return (
                      <BarColumn key={month} onMouseLeave={() => setHoveredSegment(null)}>
                        <BarStack $heightPct={heightPct}>
                          {visibleSegments.map((segment, index) => {
                            const segmentPct = total > 0 ? (segment.value / total) * 100 : 0;
                            const isActive =
                              hoveredSegment?.month === month &&
                              hoveredSegment.segment === segment.key;
                            const isTop = index === visibleSegments.length - 1;

                            return (
                              <BarSegment
                                key={segment.key}
                                $pct={segmentPct}
                                $color={segment.color}
                                $activeColor={segment.activeColor}
                                $isActive={isActive}
                                $isTop={isTop}
                                onMouseEnter={() =>
                                  setHoveredSegment({ month, segment: segment.key })
                                }
                              >
                                {isActive &&
                                  renderSegmentTooltip(month, segment.key, segment.value)}
                              </BarSegment>
                            );
                          })}
                        </BarStack>
                        <BarLabel>{MONTH_LABELS[month - 1]}</BarLabel>
                      </BarColumn>
                    );
                  })}
                </ChartArea>
              </ChartBarsFrame>
              <ChartMonthLabelsRow>
                {monthlyEarnings.map(monthData => {
                  const month = monthData.month ?? 1;
                  return <MonthLabelCell key={month}>{MONTH_LABELS[month - 1]}</MonthLabelCell>;
                })}
              </ChartMonthLabelsRow>
            </ChartPlot>
            <YAxisColumn aria-hidden>
              {chartYAxis.ticks
                .slice()
                .reverse()
                .map(tick => (
                  <YAxisLabel key={tick} $bottomPct={(tick / chartYAxis.axisMax) * 100}>
                    {formatAxisMoney(tick)}
                  </YAxisLabel>
                ))}
            </YAxisColumn>
          </ChartInner>
        </ChartWrapper>
        <ChartLegend>
          {CHART_SEGMENT_CONFIG.map(segment => {
            const yearTotal =
              segment.key === 'completed'
                ? (yearSummary?.completedEarnings ?? 0)
                : segment.key === 'upcoming'
                  ? (yearSummary?.upcomingEarnings ?? 0)
                  : segment.key === 'reimbursements'
                    ? (yearSummary?.reimbursements ?? 0)
                    : (yearSummary?.hostIncentives ?? 0);

            return (
              <LegendItem key={segment.key}>
                <LegendSwatch $color={segment.color} />
                <LegendAmount>{formatTooltipMoney(yearTotal)}</LegendAmount>
                {segment.label}
              </LegendItem>
            );
          })}
        </ChartLegend>
      </ChartCard>

      <VehicleSection>
        <VehicleSectionTitle>By vehicle</VehicleSectionTitle>
        {vehicleEarnings.length === 0 ? (
          <EmptyText>No vehicle earnings for {selectedYear}.</EmptyText>
        ) : (
          <VehicleList>
            {vehicleEarnings.map(item => {
              const imageUrl = getVehicleImageUrl(item.car);
              const statusLabel =
                earnings?.vehicleStatusOptions?.find(option => option.value === item.status)
                  ?.label ?? item.status;

              return (
                <VehicleRow key={item.car.id}>
                  {imageUrl ? (
                    <VehicleImage src={imageUrl} alt={`${item.car.make} ${item.car.model}`} />
                  ) : (
                    <VehicleImagePlaceholder>No photo</VehicleImagePlaceholder>
                  )}
                  <VehicleInfo>
                    <VehicleName>
                      <VehicleStatus $status={item.status}>{statusLabel}</VehicleStatus>
                      {[item.car.year, item.car.make, item.car.model].filter(Boolean).join(' ')}
                    </VehicleName>
                    <VehicleMeta>
                      {item.car.licensePlate
                        ? `Plate ${item.car.licensePlate}`
                        : 'No plate on file'}
                    </VehicleMeta>
                  </VehicleInfo>
                  <VehicleAmount>
                    {formatMoney(item.earnings.completedEarnings ?? 0)}
                    {earningsCode}
                  </VehicleAmount>
                </VehicleRow>
              );
            })}
          </VehicleList>
        )}
      </VehicleSection>
    </PageContainer>
  );
}
